
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useEmployees } from '@/hooks/useEmployees';
import { useAssignRoleToEmployee, useRemoveRoleFromEmployee, CustomRole } from '@/hooks/useRoles';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface AssignRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: CustomRole;
}

export function AssignRoleDialog({ open, onOpenChange, role }: AssignRoleDialogProps) {
  const { toast } = useToast();
  const { employees } = useEmployees();
  const assignRole = useAssignRoleToEmployee();
  const removeRole = useRemoveRoleFromEmployee();
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // جلب الموظفين المعينين لهذا الدور
  useEffect(() => {
    const fetchEmployeesWithRole = async () => {
      if (!role || !open) return;

      setLoading(true);
      try {
        const { data: employeeRoles, error } = await supabase
          .from('employee_roles')
          .select('employee_id')
          .eq('role_id', role.id);

        if (error) {
          console.error('Error fetching employee roles:', error);
          return;
        }

        const assignedEmployeeIds = employeeRoles?.map(er => er.employee_id) || [];
        setSelectedEmployees(assignedEmployeeIds);
      } catch (error) {
        console.error('Error in fetchEmployeesWithRole:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeesWithRole();
  }, [role?.id, open]);

  const handleEmployeeToggle = (employeeId: string) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSave = async () => {
    if (!role) return;

    setLoading(true);
    try {
      // الحصول على الموظفين المعينين حاليًا
      const { data: currentAssignments } = await supabase
        .from('employee_roles')
        .select('employee_id')
        .eq('role_id', role.id);

      const currentlyAssigned = currentAssignments?.map(assignment => assignment.employee_id) || [];

      // إزالة الدور من الموظفين غير المحددين
      const toRemove = currentlyAssigned.filter(id => !selectedEmployees.includes(id));
      
      for (const employeeId of toRemove) {
        await removeRole.mutateAsync({ employeeId, roleId: role.id });
      }

      // إضافة الدور للموظفين المحددين الجدد
      const toAdd = selectedEmployees.filter(id => !currentlyAssigned.includes(id));
      
      for (const employeeId of toAdd) {
        await assignRole.mutateAsync({ employeeId, roleId: role.id });
      }

      toast({
        title: "تم الحفظ",
        description: "تم تحديث تعيينات الأدوار بنجاح",
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error saving role assignments:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث التعيينات",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!role) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>تعيين الدور: {role.name}</DialogTitle>
          <DialogDescription>
            اختر الموظفين المراد تعيين هذا الدور لهم
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded-full border" 
              style={{ backgroundColor: role.color }}
            />
            <Badge style={{ backgroundColor: role.color, color: 'white' }}>
              {role.name}
            </Badge>
          </div>

          {loading ? (
            <div className="flex justify-center p-4">
              <div className="text-sm text-gray-500">جاري التحميل...</div>
            </div>
          ) : (
            <ScrollArea className="h-60 border rounded-lg p-3">
              <div className="space-y-3">
                {employees?.map((employee) => (
                  <div key={employee.id} className="flex items-center space-x-2 space-x-reverse">
                    <Checkbox
                      id={employee.id}
                      checked={selectedEmployees.includes(employee.id)}
                      onCheckedChange={() => handleEmployeeToggle(employee.id)}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{employee.name}</div>
                      <div className="text-sm text-gray-500">{employee.position} - {employee.department}</div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          <div className="text-sm text-gray-600">
            محدد: {selectedEmployees.length} من {employees?.length || 0} موظف
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            إلغاء
          </Button>
          <Button 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'جاري الحفظ...' : 'حفظ التعيينات'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
